/**
 * DV-DEP-001: Deployments React Query Hook
 *
 * React Query hooks for deployments functionality including finished productions,
 * YouTube integration, and deployment management.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DeploymentsAPIService } from '@/services/deployments-api';
import type {
  CreateYoutubeUploadRequest,
  ListProductionsParams,
} from '@/types/deployments';

// Query Keys
export const deploymentKeys = {
  all: ['deployments'] as const,
  productions: () => [...deploymentKeys.all, 'productions'] as const,
  productionsList: (params?: ListProductionsParams) =>
    [...deploymentKeys.productions(), 'list', params] as const,
  production: (id: string) => [...deploymentKeys.productions(), 'detail', id] as const,
  productionJobs: (id: string) => [...deploymentKeys.productions(), 'jobs', id] as const,
  youtube: () => [...deploymentKeys.all, 'youtube'] as const,
  youtubeStatus: () => [...deploymentKeys.youtube(), 'status'] as const,
  downloadUrl: (productionId: string) =>
    [...deploymentKeys.all, 'download', productionId] as const,
} as const;

// === PRODUCTIONS HOOKS ===

/**
 * Hook to fetch finished productions with optional filtering and pagination
 */
export function useProductions(params?: ListProductionsParams) {
  return useQuery({
    queryKey: deploymentKeys.productionsList(params),
    queryFn: () => DeploymentsAPIService.getProductions(params),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a specific production with its deployments
 */
export function useProduction(productionId: string) {
  return useQuery({
    queryKey: deploymentKeys.production(productionId),
    queryFn: () => DeploymentsAPIService.getProduction(productionId),
    enabled: !!productionId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get download URL for a production (on-demand, not cached)
 */
export function useProductionDownloadUrl() {
  return useMutation({
    mutationFn: (productionId: string) =>
      DeploymentsAPIService.getProductionDownloadUrl(productionId),
  });
}

/**
 * Hook to trigger production download
 */
export function useDownloadProduction() {
  return useMutation({
    mutationFn: ({ productionId, filename }: { productionId: string; filename?: string }) =>
      DeploymentsAPIService.triggerDownload(productionId, filename),
  });
}

// === YOUTUBE INTEGRATION HOOKS ===

/**
 * Hook to fetch YouTube integration status
 */
export function useYoutubeIntegration() {
  return useQuery({
    queryKey: deploymentKeys.youtubeStatus(),
    queryFn: () => DeploymentsAPIService.getYoutubeIntegrationStatus(),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to initiate YouTube OAuth flow
 */
export function useInitiateYoutubeOAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => DeploymentsAPIService.initiateYoutubeOAuth(),
    onSuccess: () => {
      // Invalidate YouTube status after OAuth initiation
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.youtubeStatus(),
      });
    },
  });
}

/**
 * Hook to disconnect YouTube integration
 */
export function useDisconnectYoutube() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => DeploymentsAPIService.disconnectYoutubeIntegration(),
    onSuccess: () => {
      // Invalidate YouTube status after disconnection
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.youtubeStatus(),
      });
    },
  });
}

// === DEPLOYMENT HOOKS ===

/**
 * Hook to create YouTube upload/deployment
 */
export function useCreateYoutubeUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productionId,
      data
    }: {
      productionId: string;
      data: CreateYoutubeUploadRequest
    }) => DeploymentsAPIService.createYoutubeUpload(productionId, data),
    onSuccess: (_, variables) => {
      // Invalidate productions list to show updated deployment status
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.productions(),
      });

      // Invalidate specific production to update its deployments
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.production(variables.productionId),
      });
    },
  });
}

/**
 * Hook to get deployment status
 */
export function useDeploymentStatus(deploymentId: string) {
  return useQuery({
    queryKey: [...deploymentKeys.all, 'deployment', deploymentId],
    queryFn: () => DeploymentsAPIService.getDeploymentStatus(deploymentId),
    enabled: !!deploymentId,
    staleTime: 5 * 1000, // 5 seconds for active deployments
    refetchInterval: (query) => {
      // Poll more frequently for active deployments
      const status = query.state.data?.deployment?.status;
      if (status === 'uploading' || status === 'processing') {
        return 5000; // 5 seconds
      }
      return false; // No polling for completed deployments
    },
  });
}

/**
 * Hook to cancel a deployment
 */
export function useCancelDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deploymentId: string) =>
      DeploymentsAPIService.cancelDeployment(deploymentId),
    onSuccess: (_, deploymentId) => {
      // Invalidate deployment status
      queryClient.invalidateQueries({
        queryKey: [...deploymentKeys.all, 'deployment', deploymentId],
      });

      // Invalidate productions to update status
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.productions(),
      });
    },
  });
}

/**
 * Hook to retry a failed deployment
 */
export function useRetryDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deploymentId: string) =>
      DeploymentsAPIService.retryDeployment(deploymentId),
    onSuccess: (_, deploymentId) => {
      // Invalidate deployment status
      queryClient.invalidateQueries({
        queryKey: [...deploymentKeys.all, 'deployment', deploymentId],
      });

      // Invalidate productions to update status
      queryClient.invalidateQueries({
        queryKey: deploymentKeys.productions(),
      });
    },
  });
}

// === UTILITY HOOKS ===

/**
 * Hook to prefetch a production's details
 */
export function usePrefetchProduction() {
  const queryClient = useQueryClient();

  return (productionId: string) => {
    queryClient.prefetchQuery({
      queryKey: deploymentKeys.production(productionId),
      queryFn: () => DeploymentsAPIService.getProduction(productionId),
      staleTime: 30 * 1000,
    });
  };
}

/**
 * Hook to invalidate all deployment-related queries
 */
export function useInvalidateDeployments() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: deploymentKeys.all,
    });
  };
}

/**
 * Hook to get count of active deployments (uploading/processing)
 */
export function useActiveDeploymentsCount() {
  const { data: productionsData } = useProductions({
    limit: 100, // Get more to count active deployments
    offset: 0,
  });

  const activeCount = productionsData?.productions?.reduce((count, production: any) => {
    const deployment = production.latestDeployment;
    if (deployment && (deployment.status === 'uploading' || deployment.status === 'processing')) {
      return count + 1;
    }
    return count;
  }, 0) || 0;

  return activeCount;
}

/**
 * Custom hook for pagination state
 */
export function useProductionsPagination(initialLimit = 20) {
  const [params, setParams] = useState<ListProductionsParams>({
    limit: initialLimit,
    offset: 0,
  });

  const nextPage = () => {
    setParams(prev => ({
      ...prev,
      offset: (prev.offset || 0) + (prev.limit || initialLimit),
    }));
  };

  const prevPage = () => {
    setParams(prev => ({
      ...prev,
      offset: Math.max(0, (prev.offset || 0) - (prev.limit || initialLimit)),
    }));
  };

  const setPage = (page: number) => {
    setParams(prev => ({
      ...prev,
      offset: page * (prev.limit || initialLimit),
    }));
  };

  const setFilters = (filters: ListProductionsParams['filters']) => {
    setParams(prev => ({
      ...prev,
      filters,
      offset: 0, // Reset to first page when filtering
    }));
  };

  const setSort = (sort: ListProductionsParams['sort']) => {
    setParams(prev => ({
      ...prev,
      sort,
      offset: 0, // Reset to first page when sorting
    }));
  };

  return {
    params,
    setParams,
    nextPage,
    prevPage,
    setPage,
    setFilters,
    setSort,
  };
}

// === DV-DEP-003: JOB TRACKING HOOKS ===

/**
 * Hook to fetch jobs for a specific production
 */
export function useProductionJobs(productionId: string) {
  return useQuery({
    queryKey: deploymentKeys.productionJobs(productionId),
    queryFn: () => DeploymentsAPIService.getProductionJobs(productionId),
    enabled: !!productionId,
    staleTime: 10 * 1000, // 10 seconds - jobs change frequently
    refetchInterval: (query) => {
      // Poll more frequently for active jobs
      const hasActiveJobs = query.state.data?.jobs?.some(job =>
        job.status === 'queued' || job.status === 'processing'
      );
      return hasActiveJobs ? 5000 : false; // 5 seconds for active jobs
    },
  });
}

